import React, { useState, useEffect } from "react"
import { Box } from "@mui/material"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import RosterTab from "./ProjectRoster/RosterTab"
import AttendanceSheetsTab from "./AttendanceSheet/AttendanceSheetTab"
import { getRostersByProjectId } from "../../utils/RosterAPI"

export default function ProjectRoster({ projectId, rosterId }) {
    const [tab, setTab] = useState(0)
    const [resolvedRosterId, setResolvedRosterId] = useState(rosterId)
    const [loadingRoster, setLoadingRoster] = useState(false)

    // Function to fetch rosters by project ID
    const fetchRosters = async () => {
        // If rosterId is already provided, use it
        if (rosterId) {
            setResolvedRosterId(rosterId)
            return
        }

        // If no projectId, can't fetch rosters
        if (!projectId) {
            return
        }

        setLoadingRoster(true)
        try {
            const response = await getRostersByProjectId(projectId)
            // API returns an array of rosters
            const rosters = Array.isArray(response) ? response : (response?.data || [])
            
            // Use the first roster if available
            if (rosters.length > 0) {
                setResolvedRosterId(rosters[0].id)
            } else {
                // No rosters found, set to null so components can handle "no roster" state
                setResolvedRosterId(null)
            }
        } catch (error) {
            console.error("Error fetching rosters by project ID:", error)
            setResolvedRosterId(null)
        } finally {
            setLoadingRoster(false)
        }
    }

    // Fetch rosters by project ID if rosterId is not provided
    useEffect(() => {
        fetchRosters()
    }, [projectId, rosterId])

    // Callback to handle when a roster is created
    const handleRosterCreated = (newRoster) => {
        if (newRoster?.id) {
            setResolvedRosterId(newRoster.id)
        }
    }

    const handleTabChange = (_, newValue) => setTab(newValue)

    return (
        <Box sx={{ mt: 3 }}>
            {/* Tabs */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, pl: 2 }}>
                <Box
                    sx={{
                        display: "inline-flex",
                        backgroundColor: "#F2F4F7",
                        borderRadius: "8px",
                        padding: "4px",
                        gap: "4px",
                    }}
                >
                    <Box
                        onClick={() => setTab(0)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            px: 2,
                            py: 1,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: 500,
                            transition: "all 0.15s ease",
                            backgroundColor: tab === 0 ? "#fff" : "transparent",
                            color: tab === 0 ? "#101828" : "#667085",
                            boxShadow: tab === 0 ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                            "&:hover": {
                                backgroundColor: tab === 0 ? "#fff" : "#E4E7EC",
                            },
                        }}
                    >
                        <PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />
                        Roster
                    </Box>
                    <Box
                        onClick={() => setTab(1)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            px: 2,
                            py: 1,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: 500,
                            transition: "all 0.15s ease",
                            backgroundColor: tab === 1 ? "#fff" : "transparent",
                            color: tab === 1 ? "#101828" : "#667085",
                            boxShadow: tab === 1 ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
                            "&:hover": {
                                backgroundColor: tab === 1 ? "#fff" : "#E4E7EC",
                        },
                    }}
                >
                        <AssignmentOutlinedIcon sx={{ fontSize: 16 }} />
                        Attendance Sheets
                    </Box>
                </Box>
            </Box>

            {/* Tab Content */}
            {tab === 0 && <RosterTab projectId={projectId} rosterId={resolvedRosterId} onRosterCreated={handleRosterCreated} />}
            {tab === 1 && <AttendanceSheetsTab rosterId={resolvedRosterId} />}
        </Box>
    )
}
